// The envelope, carried on every event whatever the type.
//
// Rule 3: the envelope answers who, where and when, and the payload answers
// what. Anything a report needs in order to group, count or scope belongs here.
//
// Section 4.1 of the schema, plus the three additions argued in 4.3, minus the
// one field reclassified in 4.2.

import * as v from "./validators.js";
import { EVENT_TYPES } from "./events.js";

/** The schema version this package writes. A breaking change moves it. */
export const SCHEMA_VERSION = 1;

export const envelope = v.object({
  // Client-generated, and the idempotency key for sync. A repeat is not an
  // error, it is a no-op.
  event_id: v.uuid(),
  event_type: v.oneOf(EVENT_TYPES),
  schema_version: v.integer(),

  // Device clock at the moment of the event, stored as received and never
  // corrected. Skew against the server clock is recorded as a data-quality
  // signal rather than fixed, per constraint 1 point 5.
  occurred_at: v.timestamp(),

  // Monotonic per device, so ordering survives clock skew. A gap is not
  // rejected: a gap is what a lost event looks like and it belongs in the
  // reconciliation report rather than in an error log.
  client_sequence: v.integer(),

  activation_id: v.uuid(),
  client_id: v.uuid(),

  // Null for a Fixture XP with no Vault.
  vault_id: v.nullable(v.uuid()),
  // Null where no format applies. A closed label at the activation level that
  // reports may filter on, which is a different thing from format_context and
  // the difference is the whole of constraint 2.
  format: v.nullable(v.string()),
  // Null for attendee-device events.
  station_id: v.nullable(v.uuid()),

  device_id: v.uuid(),

  // Null on events with no attendee, such as station health. Unique within an
  // activation and meaningless outside it. Nothing may depend on it being
  // globally unique, or cross-activation joins become possible by accident.
  attendee_ref: v.nullable(v.uuid()),

  consent_version: v.nullable(v.string()),

  // Signed at creation with a key from the device's hardware keystore.
  // Base64 on the wire, bytea in the store. Verified at ingest against the
  // registered device key, which is what makes local database tampering
  // detectable rather than invisible.
  device_signature: v.string(),
});

// received_at, spine_seq and capture_channel are deliberately absent above. All
// three are assigned by the server: a device that sent either of the first two
// would be telling the server what order its own writes happened in.
//
// capture_channel is the same shape of mistake with a sharper edge. Migration
// db/018 makes it the column that decides whether a row must carry a
// device_signature, so a device that could name its own channel could name
// 'self_serve_web' and be excused the signature that proves it is a device. The
// sync path writes 'stand_tablet' as a literal for that reason, and it is the
// only writer that produces one.
//
// sync_state is absent for a different reason, per section 4.2. On the device
// it is essential and it is in the offline design. On the server it is
// meaningless, because every row in the spine is by definition received, and
// storing it would create a mutable column on an append-only table.
export const SERVER_ASSIGNED = ["received_at", "spine_seq", "capture_channel"] as const;
export const DEVICE_LOCAL = ["sync_state"] as const;
